import React, { type ReactNode } from "react";
import { Text, Flex, type FlexProps, Link } from "@chakra-ui/react";
import { useHeaderHeight } from "contexts/header-height";
import { SidebarShare } from "./sidebar-share";

export type SidebarHeading = {
  title: string;
  href: string;
};

type SidebarProps = FlexProps & {
  headings: SidebarHeading[];
  /** Relative path to the current page, without the leading `/`. */
  path: string;
  title?: string;
  cta?: ReactNode;
};

export const sidebarWidth = "260px";

export function ArticleSidebar({ headings, path, title = "Table of contents", cta, ...props }: SidebarProps) {
  const { headerHeight } = useHeaderHeight();

  return (
    <Flex
      as="aside"
      display={{ base: "none", lg: "flex" }}
      pos="sticky"
      top={`${headerHeight + 20}px`}
      direction="column"
      gap={8}
      flexShrink={0}
      width={sidebarWidth}
      // Add left padding to avoid cutoff on input focus states
      pl="tiniest"
      // MaxHeight = headerHeight + 20px top padding + 20px bottom padding for breathing room
      maxHeight={`calc(100vh - ${headerHeight + 40}px)`}
      overflowY="auto"
      {...props}
    >
      {headings.length > 0 && (
        <Flex
          as="nav"
          aria-label={`${title}.`}
          overflow="hidden"
          maxHeight="100%"
          direction="column"
          gap={2}
          flexShrink={0}
        >
          <Text textStyle="overline1" fontWeight="semibold" ml="tiny">
            {title}
          </Text>

          <Flex direction="column">
            {headings.map(({ title, href }) => (
              <Link key={href} href={href} variant="sidebar" fontFamily="inter" lineHeight="16">
                {title}
              </Link>
            ))}
          </Flex>
        </Flex>
      )}

      <SidebarShare path={path} cta={cta} />
    </Flex>
  );
}
